import type { FilterInput } from "@/api/generated/model";
import { FilterConditionOperator } from "@/api/generated/model";

/**
 * `field eq value` for a GID-typed field, where `value` may be `null` to
 * match root-level rows (no parent/folder). The backend's filter Joi schema
 * explicitly allows `null` for non-array operators (see api/core/rest/util/
 * make-filter-schema.ts's `valueSchema` — `otherwise: base.allow(null)`), even
 * though the generated `FilterCondition.value` type says plain `string`.
 */
export function gidEqFilter(field: string, value: string | null): FilterInput {
    return {
        logic: "1",
        conditions: [{ field, operator: FilterConditionOperator.eq, value: value as unknown as string }],
    };
}

/**
 * `field eq value` for a boolean-typed field. The value goes over the wire as a
 * REAL JSON boolean (the cast is only to satisfy the generated
 * `FilterCondition.value: string`, same as `gidEqFilter`'s null cast) — it used
 * to send `String(value)`, and the backend's `coerce()` turned the string
 * `"false"` into `true`, so a `false` filter returned the exact complement of
 * what was asked for. The server now parses the string form too, but sending
 * the honest type is what makes this correct on both ends.
 */
export function boolEqFilter(field: string, value: boolean): FilterInput {
    return {
        logic: "1",
        conditions: [{ field, operator: FilterConditionOperator.eq, value: value as unknown as string }],
    };
}

/**
 * `field eq value` for a numeric field — the same cast as the two above, for the same
 * reason. It matters more here than for a GID: a data-store record's `id` is compared as a
 * real number in SQL, so sending `String(value)` would land a string on the wire and
 * Postgres would refuse the comparison rather than quietly matching nothing.
 */
export function numberEqFilter(field: string, value: number): FilterInput {
    return {
        logic: "1",
        conditions: [{ field, operator: FilterConditionOperator.eq, value: value as unknown as string }],
    };
}

/** Shifts every condition-index literal in a `logic` string by `offset` (e.g. "1 OR 2" + 2 → "3 OR 4"). */
function shiftLogic(logic: string, offset: number): string {
    return logic.replace(/\d+/g, (n) => String(Number(n) + offset));
}

/**
 * ANDs any number of filters together, renumbering `logic` to match the flattened
 * condition list. Each input's own `logic` (e.g. an internal OR group) is preserved,
 * wrapped in parens when it has more than one condition, so this composes correctly
 * with filters built by `idOrInFilter` below.
 *
 * ## Nulls are the point
 *
 * Absent inputs are dropped and an empty result is `null` — the shape every
 * `*ListParams.filter` already takes for "no filter". Almost every list page assembles
 * its filter from optional parts (a search box, a folder, a status tab), so this takes a
 * LIST and there is deliberately no two-argument variant: hand-rolled
 * `a && b ? combine(a, b) : (a ?? b)` and `length === 0 ? null : length === 1 ? [0]`
 * ladders all say the same thing, and one of them said it wrong for three filters.
 */
export function andFilters(
    filters: (FilterInput | null | undefined)[],
): FilterInput | null {
    const present = filters.filter((f): f is FilterInput => !!f && f.conditions.length > 0);
    if (present.length === 0) return null;
    if (present.length === 1) return present[0];

    const conditions: FilterInput["conditions"] = [];
    const parts: string[] = [];
    let offset = 0;
    for (const f of present) {
        parts.push(f.conditions.length > 1 ? `(${shiftLogic(f.logic, offset)})` : shiftLogic(f.logic, offset));
        conditions.push(...f.conditions);
        offset += f.conditions.length;
    }
    return { logic: parts.join(" AND "), conditions };
}

/**
 * `field eq id OR field in ids` — matches a row scoped exactly to `id`, or to
 * any of `ids` (e.g. a folder subtree's descendant ids). Used for recursive
 * folder/deployment search, where a result can live several levels below the
 * folder currently open.
 */
export function idOrInFilter(field: string, id: string | null, ids: string[]): FilterInput {
    const conditions: FilterInput["conditions"] = [
        { field, operator: FilterConditionOperator.eq, value: id as unknown as string },
    ];
    if (ids.length) conditions.push({ field, operator: FilterConditionOperator.in, value: ids as unknown as string });
    return { logic: conditions.length > 1 ? "1 OR 2" : "1", conditions };
}
